"use client";

import { Loader2, X } from "lucide-react";
import type {
  CreditBalanceModalProps,
  CreditTier,
  PurchaseMode,
} from "./credit-balance/types";
import { Dialog, DialogContent, DialogTitle } from "../../shadcn/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "../../shadcn/ui/drawer";
import React, { useEffect, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { cn, useIsDesktop } from "@punchup/utils";

import { BuyCreditsTab } from "./credit-balance/BuyCreditsTab";
import { DetailsTab } from "./credit-balance/DetailsTab";
import { HistoryTab } from "./credit-balance/HistoryTab";
import { UnderlineTabs } from "../common/UnderlineTabs";

// Re-export public types for consumers
export type {
  CreditTransactionType,
  CreditTransaction,
  CreditBalanceModalProps,
} from "./credit-balance/types";

type CreditTab = "details" | "history" | "buy-credits";

const CREDIT_TABS = [
  { value: "buy-credits", label: "Buy Credits" },
  { value: "details", label: "Details" },
  { value: "history", label: "History" },
] as const;

// ---- MAIN COMPONENT ----
export function CreditBalanceModal({
  isOpen,
  onOpenChange,
  balance,
  isLoading,
  initialTab = "buy-credits",
  onFetchCheckoutSession,
  onFetchTransactions,
  onPaymentComplete,
  userEmail,
  activeSubscription,
  onCancelSubscription,
  onUpdatePaymentMethod,
  onChangePlan,
  onChangeTier,
  onPreviewTierChange,
  comedianDisplayNameShort,
  updatedPricing = false,
}: CreditBalanceModalProps) {
  const isDesktop = useIsDesktop();
  const [activeTab, setActiveTab] = useState<CreditTab>(initialTab);
  const [buyCreditsInitialSelection, setBuyCreditsInitialSelection] = useState<{
    tier: CreditTier;
    mode: PurchaseMode;
  } | null>(null);
  const mobileSlideRef = useRef<HTMLDivElement>(null);
  const footerPortalRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef<CreditTab>(activeTab);

  // Sync initialTab if it changes (e.g., parent opens to buy-credits)
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setBuyCreditsInitialSelection(null);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) {
    return null;
  }

  const handleChangePlan = () => {
    const oppositeTier = activeSubscription?.tier === "pro" ? "starter" : "pro";
    setBuyCreditsInitialSelection({
      tier: oppositeTier,
      mode: "subscription",
    });
    setActiveTab("buy-credits");
    onChangePlan?.();
  };

  const handleRefill = () => {
    setBuyCreditsInitialSelection({
      tier: "starter",
      mode: "one_time",
    });
    setActiveTab("buy-credits");
  };

  // Shared loading state
  const loadingSpinner = (
    <div
      className={cn(
        "flex items-center justify-center",
        isDesktop ? "min-h-[520px]" : "h-full"
      )}
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  // Tab content renderers (shared between desktop and mobile)
  const buyCreditsContent = (
    <BuyCreditsTab
      onFetchCheckoutSession={onFetchCheckoutSession}
      onPaymentComplete={onPaymentComplete}
      userEmail={userEmail}
      activeSubscription={activeSubscription}
      onChangeTier={onChangeTier}
      onPreviewTierChange={onPreviewTierChange}
      initialSelection={buyCreditsInitialSelection}
      isDesktop={isDesktop}
      balance={balance}
      footerPortalRef={isDesktop ? undefined : footerPortalRef}
      updatedPricing={updatedPricing}
    />
  );

  const detailsContent = (
    <DetailsTab
      balance={balance}
      activeSubscription={activeSubscription}
      onCancelSubscription={onCancelSubscription}
      onUpdatePaymentMethod={onUpdatePaymentMethod}
      onChangePlan={handleChangePlan}
      onRefill={handleRefill}
      isDesktop={isDesktop}
    />
  );

  const historyContent = (
    <HistoryTab
      onFetchTransactions={onFetchTransactions}
      isDesktop={isDesktop}
      onSwitchTab={
        !isDesktop ? (tab: string) => setActiveTab(tab as CreditTab) : undefined
      }
    />
  );

  // ---- DESKTOP: Radix Tabs with vertical sidebar ----
  const desktopContent = (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as CreditTab)}
      className="flex flex-row flex-1 min-h-0"
    >
      <TabsList className="flex flex-col items-start h-full w-[320px] shrink-0 justify-start rounded-none bg-[#F9FAFB]/50 border-r border-[#F3F4F6] px-6 pt-10 gap-[6px]">
        {/* Punchup logo */}
        <img
          src="/resources/punchupLogoIcon.svg"
          alt="Punchup"
          width={120}
          height={20}
          className="mb-4"
        />

        {/* Subtitle text */}
        <div className="mb-4 rounded-xl bg-[#FCF2E9] px-4 py-3">
          <p className="text-sm leading-6 text-[#29333C] font-normal">
            <span className="font-bold">
              Hey {comedianDisplayNameShort ?? "there"},
            </span>{" "}
            emailing your Punchup fans is always free!
          </p>
        </div>

        {/* Tab triggers */}
        <div className="flex flex-col gap-[6px]">
          {CREDIT_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="w-[271px] h-10 justify-start rounded px-3 py-2 text-sm leading-6 font-normal text-[#29333C] transition-[background-color,box-shadow,color] duration-200 ease-[cubic-bezier(0.25,1,0.5,1)] hover:bg-white/60 data-[state=active]:bg-white data-[state=active]:shadow-[0px_0px_5px_rgba(158,157,157,0.25)] data-[state=active]:font-semibold data-[state=active]:text-[#FE5E25] motion-reduce:transition-none"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </div>
      </TabsList>

      {/* Tab content — Buy Credits forceMount sets container height on desktop, and scrolls past the 90vh cap */}
      <div
        className={cn(
          "flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden relative",
          isLoading && "p-8"
        )}
      >
        {isLoading ? (
          loadingSpinner
        ) : (
          <>
            <TabsContent
              forceMount
              value="buy-credits"
              className={cn(
                "mt-0 p-8 min-h-0 overflow-y-auto",
                activeTab !== "buy-credits" && "invisible pointer-events-none"
              )}
            >
              {buyCreditsContent}
            </TabsContent>

            <TabsContent
              value="details"
              className="mt-0 absolute inset-0 p-8 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              {detailsContent}
            </TabsContent>

            <TabsContent
              value="history"
              className="mt-0 absolute inset-0 p-8 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              {historyContent}
            </TabsContent>
          </>
        )}
      </div>
    </Tabs>
  );

  // ---- MOBILE: UnderlineTabs with slide transitions ----
  const handleMobileTabChange = (value: string) => {
    const newTab = value as CreditTab;
    if (newTab === activeTab) return;

    const tabOrder: CreditTab[] = ["buy-credits", "details", "history"];
    const oldIndex = tabOrder.indexOf(prevTabRef.current);
    const newIndex = tabOrder.indexOf(newTab);
    const direction = newIndex > oldIndex ? "right" : "left";

    prevTabRef.current = newTab;
    setActiveTab(newTab);

    // Respect prefers-reduced-motion
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Animate using CSS transition — start offset, then slide to center
    const el = mobileSlideRef.current;
    if (el && !prefersReduced) {
      el.style.transition = "none";
      el.style.opacity = "0";
      el.style.transform =
        direction === "right" ? "translateX(8%)" : "translateX(-8%)";
      // Force reflow
      el.getBoundingClientRect();
      el.style.transition =
        "transform 220ms cubic-bezier(0.25, 1, 0.5, 1), opacity 220ms ease-out";
      el.style.opacity = "1";
      el.style.transform = "translateX(0)";
    }
  };

  const mobileContent = (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
        <h1 className="text-xl font-bold text-foreground">Credit Balance</h1>
        <button
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="flex items-center justify-center h-11 w-11 -mr-2"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex items-center shrink-0">
        <UnderlineTabs
          tabs={[...CREDIT_TABS]}
          activeTab={activeTab}
          onTabChange={handleMobileTabChange}
          className="px-4 flex-1"
        />
      </div>

      <div className="flex-1 overflow-auto p-4 min-h-0">
        <div ref={mobileSlideRef}>
          {isLoading
            ? loadingSpinner
            : activeTab === "buy-credits"
              ? buyCreditsContent
              : activeTab === "details"
                ? detailsContent
                : historyContent}
        </div>
      </div>
      {/* Portal target for mobile checkout CTA — sits outside scroll, anchored to bottom by flexbox */}
      <div ref={footerPortalRef} className="shrink-0" />
    </div>
  );

  const content = isDesktop ? desktopContent : mobileContent;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="flex flex-col max-w-[874px] w-full max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]">
          <DialogTitle className="sr-only">Credit Balance</DialogTitle>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (open !== isOpen) onOpenChange(open);
      }}
    >
      <DrawerContent className="h-[100dvh] p-0 gap-0 overflow-hidden">
        <DrawerTitle className="sr-only">Credit Balance</DrawerTitle>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
